import {
  getAnacapriElementsConfig
} from "./anacapri-chunk.VM5XEEU5.js";

// src/components/dialog/DialogManager.ts
var DialogManager = class {
  constructor(config2) {
    /**
     * Current open modal dialog elements.
     */
    this.openedModalDialogs = [];
    /**
     * Current open nonmodal dialog elements.
     */
    this.openedNonModalDialogs = [];
    this.zIndexBase = config2.dialogManager?.zIndexBase ?? 2e3;
    this.inertElementSelector = `body > *:not(dialog, ${config2.elementPrefix ? `${config2.elementPrefix}-` : ""}ana-dialog, #salemove, .ReactModalPortal, #quickEnrollWidget, [data-ana-inert-ignore])`;
    this.ignoreInertElementSelector = config2.dialogManager?.ignoreInertElementSelector;
  }
  /**
   * Push an element to the opened dialogs array and add appropriate
   * styles to hide overflow on the body element.
   * @param dialog element that was opened.
   * @returns promise that resolves once all dialogs have animated to their new
   * positions
   */
  openDialog(dialog) {
    if (dialog.type === "nonmodal" /* NonModal */) {
      this.openedNonModalDialogs.unshift(dialog);
    } else {
      this.openedModalDialogs.unshift(dialog);
      document.body.classList.add("ao-modal-open");
      this.trapInteractivityToActiveDialog(true);
      if (document.body.scrollHeight > window.innerHeight) {
        document.body.style.paddingRight = "18px";
      }
    }
    return this.updateDialogStack();
  }
  /**
   * Filters opened dialogs to remove passed in dialog from
   * open dialogs. Removes styles from body element that hide
   * overflow if the dialog removed was the last opened.
   * @param dialog element to be removed from open dialogs.
   * @returns promise that resolves once all dialogs have animated to their new
   * positions
   */
  closeDialog(dialog) {
    if (dialog.type === "nonmodal" /* NonModal */) {
      this.openedNonModalDialogs = this.openedNonModalDialogs.filter(
        (openedDialog) => openedDialog !== dialog
      );
    } else {
      this.openedModalDialogs = this.openedModalDialogs.filter(
        (openedDialog) => openedDialog !== dialog
      );
      if (this.openedModalDialogs.length === 0) {
        document.body.classList.remove("ao-modal-open");
        document.body.style.removeProperty("padding-right");
        this.trapInteractivityToActiveDialog(false);
      }
    }
    return this.updateDialogStack();
  }
  /**
   * Update the position of the dialogs in the dialog stack
   * based on the currently opened dialog order.
   * @returns promise that resolves once all dialogs have animated to their new
   * positions
   */
  updateDialogStack() {
    const updates = [];
    for (const [index, dialog] of [...this.openedModalDialogs].entries()) {
      updates.push(
        dialog.updatePositionInDialogStack(
          index,
          dialog.zIndex ?? this.zIndexBase + this.openedModalDialogs.length - index - 1
        )
      );
    }
    return Promise.all(updates);
  }
  trapInteractivityToActiveDialog(enabled) {
    const inertElements = document.querySelectorAll(this.inertElementSelector);
    const filter = this.ignoreInertElementSelector;
    for (const element of inertElements) {
      if (enabled && !(filter && element.matches(filter))) {
        element.setAttribute("inert", "");
      } else {
        element.removeAttribute("inert");
      }
    }
  }
  /**
   * Get the currently opened modal dialog elements.
   * @returns opened dialog elements.
   */
  getModalDialogs() {
    return this.openedModalDialogs;
  }
  /**
   * Get the front most active dialog open, including both modal and nonmodal.
   * @returns active dialog
   */
  getActiveDialog() {
    const activeElement = document.activeElement;
    for (const dialog of this.openedNonModalDialogs) {
      if (dialog.contains(activeElement) || dialog === activeElement) {
        return dialog;
      }
    }
    if (this.openedModalDialogs.length > 0) {
      return this.openedModalDialogs[0];
    }
    return null;
  }
  /**
   * Scrolls to the top of the currently active dialog
   * @param options {ScrollToOptions} - @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo
   */
  scrollToTopOfActiveDialog(options = {}) {
    const dialog = this.getActiveDialog();
    dialog?.scrollToTop(options);
  }
  /**
   * Checks whether the dialog element was the last dialog
   * element to be opened.
   * @param dialog element to test against open dialogs.
   * @returns true if dialog is or contains the active element.
   */
  isActiveDialog(dialog) {
    return dialog === this.getActiveDialog();
  }
  /**
   * Resets DialogManager to it's initial state. Could be useful for a close
   * all dialogs feature, but currently is only used to for testing purposes.
   */
  reset() {
    this.openedNonModalDialogs = [];
    this.openedModalDialogs = [];
    document.body.classList.remove("ao-modal-open");
    document.body.style.removeProperty("padding-right");
    this.trapInteractivityToActiveDialog(false);
  }
};
var config = getAnacapriElementsConfig();
var newDialogManager = new DialogManager(config);

export {
  DialogManager,
  newDialogManager
};
